import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import AppBar from '@material-ui/core/AppBar'
import IconButton from '@material-ui/core/IconButton'
import Badge from '@material-ui/core/Badge'
import Box from '@material-ui/core/Box'
import Toolbar from '@material-ui/core/Toolbar'
import Drawer from '@material-ui/core/Drawer'
import List from '@material-ui/core/List'
import MenuIcon from 'components/material-symbol/icons/Menu'
import MenuOpenIcon from 'components/material-symbol/icons/MenuOpen'
import LeftPanelCloseIcon from 'components/material-symbol/icons/LeftPanelClose'
import { useTheme } from '@material-ui/core/styles'
import { useLocation } from 'react-router-dom'
import AccountDropdown from 'components/account-dropdown'
import Footer from 'components/footer'
import BrandSwitcher from './brand-switcher'
import SidebarMenuItem from './sidebar-menu-item'
import useStyles from './styles'
import {
  getSidebarLayoutWidth,
  shouldShowNavLabels,
} from '../app-shell/sidebar-layout'
import { useIsCollapsibleSidebar, useIsDesktopNav } from 'lib/hooks'
import { getAssumedUser } from 'lib/auth/impersonation'
import clsx from 'clsx'

export default function Shell({
  logo = { src: '', alt: '' },
  menuItems = [],
  showBrandSwitcher = true,
  showAccountDropdown = true,
  showSidebar,
  toolbarContent = null,
  unviewedLeadCount = null,
  showFooter = false,
  children,
}) {
  const assumedUser = getAssumedUser()
  const location = useLocation()
  const hasSidebar = showSidebar ?? menuItems.length > 0
  const theme = useTheme()
  const isCollapsibleSidebar = useIsCollapsibleSidebar()
  const isDesktopNav = useIsDesktopNav()
  const isMobileNav = !isDesktopNav && !isCollapsibleSidebar

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const [isTabletNavOpen, setIsTabletNavOpen] = useState(true)

  const isNavCollapsed = isCollapsibleSidebar && !isTabletNavOpen
  const sidebarLayoutWidth = getSidebarLayoutWidth(theme, {
    isDesktopNav,
    isCollapsibleSidebar,
    isNavCollapsed,
  })
  const showNavLabels = shouldShowNavLabels({
    isDesktopNav,
    isMobileNav,
    isNavCollapsed,
  })
  const classes = useStyles({
    assumedUser,
    showFooter,
    hasSidebar,
    sidebarLayoutWidth,
    isNavCollapsed,
  })

  const handleDrawerToggle = () => {
    if (isMobileNav) {
      setIsMobileNavOpen(!isMobileNavOpen)
      return
    }
    if (isCollapsibleSidebar) {
      setIsTabletNavOpen(!isTabletNavOpen)
    }
  }

  useEffect(() => {
    if (isMobileNav) {
      setIsTabletNavOpen(true)
      return
    }
    if (isDesktopNav) {
      setIsTabletNavOpen(true)
      return
    }
    if (isCollapsibleSidebar) {
      setIsTabletNavOpen(false)
    }
  }, [isCollapsibleSidebar, isMobileNav, isDesktopNav])

  const menu = (
    <List className={classes.menu}>
      {menuItems.map(item => {
        const MenuItemComponent = item?.component
          ? item.component
          : SidebarMenuItem
        return (
          <MenuItemComponent
            {...{
              onClick: isMobileNav ? handleDrawerToggle : undefined,
              key: item.path || item.label,
              ...item,
              location,
              isCollapsibleSidebar,
              isNavCollapsed,
              showNavLabels,
              isTabletNavOpen,
              setIsTabletNavOpen,
            }}
          />
        )
      })}
    </List>
  )

  const drawerContainer =
    window !== undefined ? () => window.document.body : undefined

  return (
    <>
      {theme.palette.body?.background && (
        <Helmet>
          <style>{`body { background-color: ${theme.palette.body.background}; }`}</style>
        </Helmet>
      )}
      <Box display="flex">
        <AppBar position="fixed" square className={classes.appBar}>
          <Toolbar
            className={clsx(classes.toolbar, {
              [classes.toolbarCollapsibleSidebar]: isCollapsibleSidebar,
            })}
          >
            {hasSidebar && (
              <IconButton
                color="primary"
                aria-label="open drawer"
                edge={isMobileNav ? 'start' : false}
                onClick={handleDrawerToggle}
                className={
                  isMobileNav
                    ? classes.mobileMenuButton
                    : classes.tabletMenuButton
                }
              >
                <Badge
                  invisible={!location.pathname.startsWith('/pga-coach')}
                  classes={{
                    badge: clsx(
                      classes.mobileMenuButtonBadge,
                      unviewedLeadCount > 0 && classes.unviewedLeadBadge,
                    ),
                  }}
                  color={unviewedLeadCount > 0 ? 'default' : 'primary'}
                  variant="dot"
                  overlap="rectangular"
                >
                  {isMobileNav ? (
                    <MenuIcon color="primary" opticalSize={24} />
                  ) : !isTabletNavOpen ? (
                    <MenuOpenIcon color="primary" opticalSize={24} />
                  ) : (
                    <LeftPanelCloseIcon color="primary" opticalSize={24} />
                  )}
                </Badge>
              </IconButton>
            )}
            {toolbarContent || (
              <>
                {showBrandSwitcher ? (
                  <BrandSwitcher logo={logo} />
                ) : (
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    style={{
                      display: 'block',
                      width:
                        (isMobileNav
                          ? logo.mobileWidth ?? logo.width
                          : logo.width) ?? '110px',
                      height:
                        (isMobileNav
                          ? logo.mobileHeight ?? logo.height
                          : logo.height) ?? 'auto',
                    }}
                  />
                )}
                {showAccountDropdown && !isMobileNav && <Box flexGrow={1} />}
                {showAccountDropdown && <AccountDropdown />}
              </>
            )}
          </Toolbar>
        </AppBar>
        {hasSidebar && (
          <nav
            className={clsx(classes.drawerContainer, {
              [classes.drawerContainerHidden]: sidebarLayoutWidth === 0,
            })}
            style={
              sidebarLayoutWidth > 0 ? { width: sidebarLayoutWidth } : undefined
            }
          >
            {isDesktopNav && (
              <Drawer
                color="primary"
                variant="persistent"
                open
                classes={{
                  paper: clsx(classes.drawerPaper, classes.drawerOpen),
                }}
              >
                <div className={classes.toolbarSpacing} />
                {menu}
              </Drawer>
            )}
            {!isDesktopNav && isMobileNav && (
              <Drawer
                container={drawerContainer}
                variant="temporary"
                anchor="left"
                open={isMobileNavOpen}
                onClose={handleDrawerToggle}
                classes={{
                  paper: classes.drawerPaper,
                }}
                ModalProps={{
                  keepMounted: true, // Better open performance on mobile.
                }}
              >
                {menu}
              </Drawer>
            )}
            {!isDesktopNav && isCollapsibleSidebar && (
              <Drawer
                variant="permanent"
                open={isTabletNavOpen}
                classes={{
                  paper: clsx(classes.drawerPaper, {
                    [classes.drawerOpen]: !isNavCollapsed,
                    [classes.drawerClose]: isNavCollapsed,
                  }),
                }}
              >
                <div className={classes.toolbarSpacing} />
                {menu}
              </Drawer>
            )}
          </nav>
        )}
        <main className={classes.mainContent}>
          <div className={classes.toolbarSpacing} />
          {children}
          {showFooter && <Footer />}
        </main>
      </Box>
    </>
  )
}
